import React, { useCallback, useRef } from 'react';
import type { CustomFilterProps } from 'ag-grid-react';
import { useGridFilter } from 'ag-grid-react';
import { FILTER_STYLES } from '../AgGridCompliance.config';

type IdentityColumnFilterModel = {
  filterType: 'identityColumn';
  filterValues: {
    activeProfile: string;
    recentActivity: string;
    mfaStatus: string;
  };
} | null;

const OPTIONS = {
  activeProfile: {
    label: 'Active Profile',
    options: [
      { value: 'all', label: 'All' },
      { value: 'hasActive', label: 'Has Profile' },
      { value: 'missingActive', label: 'Missing Profile' },
    ],
  },
  recentActivity: {
    label: 'Recent Activity',
    options: [
      { value: 'all', label: 'All' },
      { value: 'active', label: 'Active (< 30 days)' },
      { value: 'inactive', label: 'Inactive (> 30 days)' },
    ],
  },
  mfaStatus: {
    label: 'MFA Status',
    options: [
      { value: 'all', label: 'All' },
      { value: 'strongMfa', label: 'Strong MFA' },
      { value: 'weakMfa', label: 'Weak MFA' },
      { value: 'noMfa', label: 'No MFA' },
    ],
  },
} as const;

const KEYS = ['activeProfile', 'recentActivity', 'mfaStatus'] as const;

function getValue(model: IdentityColumnFilterModel, key: (typeof KEYS)[number]): string {
  if (!model?.filterValues) {
    return 'all';
  }
  return model.filterValues[key] || 'all';
}

export const IdentityColumnFilter = ({
  model,
  onModelChange,
}: CustomFilterProps<any, any, IdentityColumnFilterModel>) => {
  const refFirst = useRef<HTMLSelectElement>(null);

  const filterValues: Record<string, string> = {};
  KEYS.forEach((key) => {
    filterValues[key] = getValue(model, key);
  });

  const doesFilterPass = useCallback(() => true, []);
  const afterGuiAttached = useCallback(() => {
    refFirst.current?.focus();
  }, []);
  useGridFilter({ doesFilterPass, afterGuiAttached });

  const updateModel = (key: string, value: string) => {
    const next = { ...filterValues, [key]: value };
    const hasActive = Object.values(next).some((v) => v !== 'all');
    if (!hasActive) {
      onModelChange(null);
      return;
    }
    onModelChange({
      filterType: 'identityColumn',
      filterValues: {
        activeProfile: next.activeProfile,
        recentActivity: next.recentActivity,
        mfaStatus: next.mfaStatus,
      },
    });
  };

  const handleReset = () => onModelChange(null);

  const handleChange = (key: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateModel(key, e.target.value);
  };

  const handleFocus = (e: React.FocusEvent<HTMLSelectElement | HTMLButtonElement>) => {
    e.currentTarget.style.border = FILTER_STYLES.focusBorder;
  };

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement | HTMLButtonElement>) => {
    e.currentTarget.style.border = FILTER_STYLES.defaultBorder;
  };

  return (
    <div style={FILTER_STYLES.container}>
      <div style={FILTER_STYLES.section}>
        <h4 style={FILTER_STYLES.sectionHeading}>Identity</h4>
        {KEYS.map((filterKey, idx) => {
          const config = OPTIONS[filterKey];
          return (
            <div key={filterKey} style={FILTER_STYLES.fieldGroup}>
              <label style={FILTER_STYLES.label}>{config.label}:</label>
              <select
                ref={idx === 0 ? refFirst : undefined}
                style={FILTER_STYLES.select}
                value={filterValues[filterKey]}
                onChange={handleChange(filterKey)}
                onFocus={handleFocus}
                onBlur={handleBlur}
              >
                {config.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
      <div style={FILTER_STYLES.resetButtonContainer}>
        <button type="button" style={FILTER_STYLES.resetButton} onClick={handleReset} onFocus={handleFocus} onBlur={handleBlur}>
          Reset All
        </button>
      </div>
    </div>
  );
};

export default IdentityColumnFilter;
